import React from 'react';
import { useParams } from 'react-router-dom';

import { snackbar } from '@occc-app/design-system';
import {
  useGetSurveysByWalletId,
  useGetWallet,
} from '@occc-app/dataAccessLayer/steeringWallet/queries';
import { useGetProductGroupById } from '@occc-app/dataAccessLayer/productGroup/queries';
import { useChainId } from '@occc-app/Chain/hooks';
import {
  useSteeringWalletActivate,
  useSteeringWalletArchive,
  useSteeringWalletDeactivate,
  useSteeringWalletUnarchive,
} from '@occc-app/dataAccessLayer/steeringWallet/mutations';
import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';

import { WalletDetailsPage } from './WalletDetailsPage';

import { mapWalletData } from '../utils';
import { ArchiveWalletConfirmDialog } from '../components/ArchiveWalletConfirmDialog';
import { messages } from '../messages';
import { DeactivateWalletConfirmDialog } from '../components/DeactivateWalletConfirmDialog';
import { UnarchiveWalletConfirmDialog } from '../components/UnarchiveWalletConfirmDialog';

export const WalletDetailsPageContainer: React.FC = () => {
  const [isArchiveConfirmation, setIsArchiveConfirmation] = React.useState(false);
  const [isDeactivateConfirmation, setIsDeactivateConfirmation] = React.useState(false);
  const [isUnarchiveConfirmation, setIsUnarchiveConfirmation] = React.useState(false);

  const chainId = useChainId();
  const { id: walletId = '' } = useParams<{ id: string }>();

  const walletResult = useGetWallet(walletId);
  const surveyResult = useGetSurveysByWalletId(walletId);

  const isUserPermittedToEditWallet = useIsUserPermittedTo(Feature.WALLET_EDIT);
  const isUserPermittedToViewWalletConfiguration = useIsUserPermittedTo(
    Feature.WALLET_EDIT_VIEW_ONLY,
  );
  const isUserPermittedToArchiveWallet = useIsUserPermittedTo(Feature.WALLET_ARCHIVE);

  const productGroupResult = useGetProductGroupById(
    walletResult.data?.steeringWallet.configuration.productGroupId,
    chainId,
  );

  const walletData = React.useMemo(
    () => mapWalletData(walletResult, surveyResult, productGroupResult),
    [walletResult, surveyResult, productGroupResult],
  );

  const onActivateError = React.useCallback(() => {
    snackbar.error(messages.activateWalletError);
  }, []);

  const onActivateCompleted = React.useCallback(() => {
    snackbar.success(messages.activateWalletSuccess);
  }, []);

  const [activateWallet, { loading: activateLoading }] = useSteeringWalletActivate({
    onCompleted: onActivateCompleted,
    onError: onActivateError,
    refetchQueries: ['steeringWallet'],
  });

  const onDeactivateError = React.useCallback(() => {
    snackbar.error(messages.deactivateWalletError);
  }, []);

  const onCloseDeactivateConfirmationDialog = React.useCallback(() => {
    setIsDeactivateConfirmation(false);
  }, []);

  const onDeactivateCompleted = React.useCallback(() => {
    snackbar.success(messages.deactivateWalletSuccess);
    onCloseDeactivateConfirmationDialog();
  }, [onCloseDeactivateConfirmationDialog]);

  const [deactivateWallet, { loading: deactivateLoading }] = useSteeringWalletDeactivate({
    onCompleted: onDeactivateCompleted,
    onError: onDeactivateError,
    refetchQueries: ['steeringWallet'],
  });

  const onArchiveError = React.useCallback(() => {
    snackbar.error(messages.archiveWalletError);
  }, []);

  const onCloseArchiveConfirmationDialog = React.useCallback(() => {
    setIsArchiveConfirmation(false);
  }, []);

  const onArchiveCompleted = React.useCallback(() => {
    snackbar.success(messages.archiveWalletSuccess);
    onCloseArchiveConfirmationDialog();
  }, [onCloseArchiveConfirmationDialog]);

  const [archiveWallet, { loading: archiveLoading }] = useSteeringWalletArchive({
    onCompleted: onArchiveCompleted,
    onError: onArchiveError,
    refetchQueries: ['steeringWallet'],
  });

  const onUnarchiveError = React.useCallback(() => {
    snackbar.error(messages.unarchiveWalletError);
  }, []);

  const onCloseUnarchiveConfirmationDialog = React.useCallback(() => {
    setIsUnarchiveConfirmation(false);
  }, []);

  const onUnarchiveCompleted = React.useCallback(() => {
    snackbar.success(messages.unarchiveWalletSuccess);
    onCloseUnarchiveConfirmationDialog();
  }, [onCloseUnarchiveConfirmationDialog]);

  const [unarchiveWallet, { loading: unarchiveLoading }] = useSteeringWalletUnarchive({
    onCompleted: onUnarchiveCompleted,
    onError: onUnarchiveError,
    refetchQueries: ['steeringWallet'],
  });

  const onArchiveWallet = React.useCallback(() => {
    archiveWallet({
      variables: {
        uuid: walletId,
      },
    });
  }, [walletId, archiveWallet]);

  const onUnarchiveWallet = React.useCallback(() => {
    unarchiveWallet({
      variables: {
        uuid: walletId,
      },
    });
  }, [walletId, unarchiveWallet]);

  const onActivateWallet = React.useCallback(() => {
    if (activateLoading) {
      return;
    }

    activateWallet({
      variables: {
        uuid: walletId,
      },
    });
  }, [walletId, activateWallet, activateLoading]);

  const onDeactivateWallet = React.useCallback(() => {
    if (deactivateLoading) {
      return;
    }

    deactivateWallet({
      variables: {
        uuid: walletId,
      },
    });
  }, [walletId, deactivateWallet, deactivateLoading]);

  const openArchiveConfirmationDialog = React.useCallback(() => {
    setIsArchiveConfirmation(true);
  }, []);

  const openUnarchiveConfirmationDialog = React.useCallback(() => {
    setIsUnarchiveConfirmation(true);
  }, []);

  const openDeactivateConfirmationDialog = React.useCallback(() => {
    setIsDeactivateConfirmation(true);
  }, []);

  const walletName = walletData.wallet.data?.name ?? '';

  return (
    <>
      <WalletDetailsPage
        walletData={walletData}
        isUserPermittedToEditWallet={isUserPermittedToEditWallet}
        isUserPermittedToArchiveWallet={isUserPermittedToArchiveWallet}
        isUserPermittedToViewWalletConfiguration={isUserPermittedToViewWalletConfiguration}
        onActivate={onActivateWallet}
        onDeactivate={openDeactivateConfirmationDialog}
        onArchive={openArchiveConfirmationDialog}
        onUnarchive={openUnarchiveConfirmationDialog}
      />

      {isArchiveConfirmation && (
        <ArchiveWalletConfirmDialog
          onClose={onCloseArchiveConfirmationDialog}
          onConfirm={onArchiveWallet}
          loading={archiveLoading}
          walletName={walletName}
        />
      )}

      {isDeactivateConfirmation && (
        <DeactivateWalletConfirmDialog
          onClose={onCloseDeactivateConfirmationDialog}
          onConfirm={onDeactivateWallet}
          loading={deactivateLoading}
          walletName={walletName}
        />
      )}

      {isUnarchiveConfirmation && (
        <UnarchiveWalletConfirmDialog
          onClose={onCloseUnarchiveConfirmationDialog}
          onConfirm={onUnarchiveWallet}
          loading={unarchiveLoading}
          walletName={walletName}
        />
      )}
    </>
  );
};
